import React, { useState } from 'react';
import { useQuery } from 'react-query';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';

import { fetchMonitoringReclamationsAvoir, fetchMonitoringCommandesAvoir } from 'utils/ts-queries';
import Client from 'components/Shared/Client';
import MonitoringCommandeRow from './MonitoringCommandeRow';

const NB_PAR_PAGE = 10;

const formatDate = (iso: string | null): string => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('fr-FR');
};

// Rendu de la pièce d'avoir (texte) : numéro de pièce, ou (legacy) si absent.
const renderPieceAvoir = (numeroPieceAvoir: string | null) => {
    return numeroPieceAvoir ?? <em>(legacy)</em>;
};

function AdministrationMonitoringAvoir() {
    const [pageRecla, setPageRecla] = useState(0);
    const [pageCmd, setPageCmd] = useState(0);

    const reclaQuery = useQuery(
        ['monitoringReclamationsAvoir', pageRecla],
        () => fetchMonitoringReclamationsAvoir(pageRecla, NB_PAR_PAGE),
        { refetchOnWindowFocus: false, keepPreviousData: true }
    );

    const cmdQuery = useQuery(
        ['monitoringCommandesAvoir', pageCmd],
        () => fetchMonitoringCommandesAvoir(pageCmd, NB_PAR_PAGE),
        { refetchOnWindowFocus: false, keepPreviousData: true }
    );

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Monitoring des avoirs</Typography>

            <Grid container spacing={2}>
                {/* Tableau de gauche : réclamations ayant eu un avoir */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 1 }}>
                        <Typography variant="subtitle1" sx={{ p: 1 }}>Dernières mises à jour par un avoir</Typography>

                        {reclaQuery.isLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
                        )}
                        {reclaQuery.isError && (
                            <Alert severity="error">Erreur de chargement des réclamations.</Alert>
                        )}
                        {reclaQuery.data && (
                            <>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Dossier</TableCell>
                                            <TableCell>Client</TableCell>
                                            <TableCell>Pièce avoir</TableCell>
                                            <TableCell>Date</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reclaQuery.data.avoirs.map(av => {
                                            // Avoir de la ligne + autres avoirs du dossier, en sous-lignes pièce | date alignées.
                                            const tousAvoirs = [
                                                { actionId: av.actionId, dateAvoir: av.dateAvoir, numeroPieceAvoir: av.numeroPieceAvoir, cleAvoir: av.cleAvoir },
                                                ...av.autresAvoirs,
                                            ];
                                            return (
                                                <TableRow key={av.actionId} hover sx={{ height: 53, '& td': { verticalAlign: 'top' } }}>
                                                    <TableCell>
                                                        <Link
                                                            href={`/reclamations/${av.reclamationId}#historique#action_${av.actionId}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {av.reclamationId}
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ '& p': { m: 0 } }}>
                                                            <Client client={av.client} format="code-libelle" withTooltip />
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                        {tousAvoirs.map(p => (
                                                            <Box key={p.actionId} sx={{ mb: 0.25, height: 30, display: 'flex', alignItems: 'center' }}>
                                                                {renderPieceAvoir(p.numeroPieceAvoir)}
                                                            </Box>
                                                        ))}
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                        {tousAvoirs.map(p => (
                                                            <Box key={p.actionId} sx={{ mb: 0.25, height: 30, display: 'flex', alignItems: 'center' }}>
                                                                <Link
                                                                    href={`/reclamations/${av.reclamationId}#historique#action_${p.actionId}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    {formatDate(p.dateAvoir)}
                                                                </Link>
                                                            </Box>
                                                        ))}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                                <TablePagination
                                    component="div"
                                    count={reclaQuery.data.nbTotal}
                                    page={pageRecla}
                                    onPageChange={(_e, p) => setPageRecla(p)}
                                    rowsPerPage={NB_PAR_PAGE}
                                    rowsPerPageOptions={[NB_PAR_PAGE]}
                                />
                            </>
                        )}
                    </Paper>
                </Grid>

                {/* Tableau de droite : CommandeGestion rattachées à un dossier */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 1 }}>
                        <Typography variant="subtitle1" sx={{ p: 1 }}>Dernières pièces d'avoir reçues</Typography>

                        {cmdQuery.isLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
                        )}
                        {cmdQuery.isError && (
                            <Alert severity="error">Erreur de chargement des commandes.</Alert>
                        )}
                        {cmdQuery.data && (
                            <>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Dossier</TableCell>
                                            <TableCell>Pièce</TableCell>
                                            <TableCell>Origine</TableCell>
                                            <TableCell>Dépôt</TableCell>
                                            <TableCell>Intégration</TableCell>
                                            <TableCell>Traitement avoir</TableCell>
                                            <TableCell align="right">JSON</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {cmdQuery.data.commandes.map(c => (
                                            <MonitoringCommandeRow key={c.cle} commande={c} />
                                        ))}
                                    </TableBody>
                                </Table>
                                <TablePagination
                                    component="div"
                                    count={cmdQuery.data.nbTotal}
                                    page={pageCmd}
                                    onPageChange={(_e, p) => setPageCmd(p)}
                                    rowsPerPage={NB_PAR_PAGE}
                                    rowsPerPageOptions={[NB_PAR_PAGE]}
                                />
                            </>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

export default AdministrationMonitoringAvoir;
